import { z } from 'zod';
import { ColorSchema, rgb, type Color } from './color.js';
import { addDays, dateKey, isLeapYear, keyToDayNumber, parseDateKey, isValidDateKey } from './dates.js';

export const EventKindSchema = z.enum(['public', 'school', 'personal', 'business', 'custom']);
export type EventKind = z.infer<typeof EventKindSchema>;

const DateKeyStr = z.string().refine(isValidDateKey, 'Expected a valid YYYY-MM-DD date');

export const EventItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().max(120),
  /** date: a single year-specific day; range: inclusive start–end; recurring: every year on month/day. */
  type: z.enum(['date', 'range', 'recurring']),
  date: DateKeyStr.optional(),
  start: DateKeyStr.optional(),
  end: DateKeyStr.optional(),
  month: z.number().int().min(1).max(12).optional(),
  day: z.number().int().min(1).max(31).optional(),
  /** True for an additional observed day (e.g. Monday after a Sunday public holiday). */
  observed: z.boolean().optional(),
  /** For an observed day: the date of the original holiday. */
  originalDate: DateKeyStr.optional(),
  /** Specially declared (once-off) holiday. */
  declared: z.boolean().optional(),
  origin: z.enum(['official', 'import', 'manual', 'calculated']).optional(),
  note: z.string().max(500).optional(),
});
export type EventItem = z.infer<typeof EventItemSchema>;

export const EventDisplaySchema = z.object({
  visible: z.boolean(),
  showLabel: z.boolean(),
  shade: z.boolean(),
  legend: z.boolean(),
  textColor: ColorSchema,
  background: ColorSchema,
});
export type EventDisplay = z.infer<typeof EventDisplaySchema>;

/** What to do with a recurring 29 February event in a non-leap year. */
export const LeapPolicySchema = z.enum(['feb28', 'mar1', 'skip']);
export type LeapPolicy = z.infer<typeof LeapPolicySchema>;
export const LEAP_POLICY_LABELS: Record<LeapPolicy, string> = {
  feb28: 'Show on 28 February in non-leap years',
  mar1: 'Show on 1 March in non-leap years',
  skip: 'Only show in leap years',
};

export const VerificationSchema = z.enum(['verified', 'unverified', 'manual']);
export type Verification = z.infer<typeof VerificationSchema>;

export const EventSetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(120),
  kind: EventKindSchema,
  year: z.number().int().nullable(),
  /** e.g. "DBE public schools (all provinces)" or "Independent school X". */
  calendarType: z.string().max(200).optional(),
  source: z.string().max(1000),
  sourceUrl: z.string().max(500).optional(),
  verification: VerificationSchema,
  verifiedAt: z.string().optional(),
  verificationNote: z.string().max(2000).optional(),
  leapPolicy: LeapPolicySchema,
  display: EventDisplaySchema,
  events: z.array(EventItemSchema),
  /** Present on project snapshots: which library set and version it was copied from. */
  libraryId: z.string().optional(),
  libraryVersion: z.number().int().optional(),
  snapshotAt: z.string().optional(),
});
export type EventSet = z.infer<typeof EventSetSchema>;

export const DEFAULT_DISPLAY: Record<EventKind, EventDisplay> = {
  public: { visible: true, showLabel: true, shade: true, legend: false, textColor: rgb('#b3261e'), background: rgb('#fde7e4') },
  school: { visible: true, showLabel: false, shade: true, legend: false, textColor: rgb('#1d5c96'), background: rgb('#e3eef9') },
  personal: { visible: true, showLabel: true, shade: false, legend: false, textColor: rgb('#7a3e9d'), background: rgb('#f3e8fa') },
  business: { visible: true, showLabel: true, shade: false, legend: false, textColor: rgb('#2f6b3a'), background: rgb('#e5f3e7') },
  custom: { visible: true, showLabel: true, shade: false, legend: false, textColor: rgb('#444444'), background: rgb('#f0f0f0') },
};

export interface Occurrence {
  date: string;
  label: string;
  eventId: string;
  setId: string;
  setKind: EventKind;
  display: EventDisplay;
  observed: boolean;
}

/** Resolve the date of a recurring month/day event in a given year (may be null when skipped). */
export function recurringDate(year: number, month: number, day: number, policy: LeapPolicy): string | null {
  if (month === 2 && day === 29 && !isLeapYear(year)) {
    if (policy === 'feb28') return dateKey(year, 2, 28);
    if (policy === 'mar1') return dateKey(year, 3, 1);
    return null;
  }
  const v = parseDateKey(dateKey(year, month, day));
  return v ? dateKey(year, month, day) : null;
}

/**
 * Expand visible event sets into date → occurrences between two inclusive date
 * keys. Ranges that cross month or year boundaries are clipped to the window.
 */
export function expandEvents(sets: EventSet[], fromKey: string, toKey: string): Map<string, Occurrence[]> {
  const out = new Map<string, Occurrence[]>();
  const from = keyToDayNumber(fromKey);
  const to = keyToDayNumber(toKey);
  const push = (date: string, set: EventSet, ev: EventItem) => {
    const dn = keyToDayNumber(date);
    if (dn < from || dn > to) return;
    const list = out.get(date) ?? [];
    list.push({
      date,
      label: ev.label,
      eventId: ev.id,
      setId: set.id,
      setKind: set.kind,
      display: set.display,
      observed: !!ev.observed,
    });
    out.set(date, list);
  };
  const fromYear = +fromKey.slice(0, 4);
  const toYear = +toKey.slice(0, 4);
  for (const set of sets) {
    if (!set.display.visible) continue;
    for (const ev of set.events) {
      if (ev.type === 'date' && ev.date) push(ev.date, set, ev);
      else if (ev.type === 'range' && ev.start && ev.end) {
        const s = Math.max(keyToDayNumber(ev.start), from);
        const e = Math.min(keyToDayNumber(ev.end), to);
        for (let dn = s; dn <= e; dn++) push(addDays(ev.start, dn - keyToDayNumber(ev.start)), set, ev);
      } else if (ev.type === 'recurring' && ev.month && ev.day) {
        for (let y = fromYear; y <= toYear; y++) {
          const d = recurringDate(y, ev.month, ev.day, set.leapPolicy);
          if (d) push(d, set, ev);
        }
      }
    }
  }
  return out;
}

/** Background priority tiers, highest first. Shown in the UI. */
export const BACKGROUND_PRIORITY = [
  { tier: 'override', label: 'Individual day colour (set on a specific date)' },
  { tier: 'public', label: 'Public holiday shading' },
  { tier: 'school', label: 'School holiday shading' },
  { tier: 'other', label: 'Other shaded events (birthdays, business, custom)' },
  { tier: 'weekend', label: 'Saturday / Sunday styling' },
  { tier: 'weekday', label: 'Ordinary weekday styling' },
] as const;
export type BackgroundTier = (typeof BACKGROUND_PRIORITY)[number]['tier'];

export interface CellBackground {
  tier: BackgroundTier;
  fill: Color | null;
  /** Date number colour if the winning tier defines one. */
  textColor: Color | null;
}

export function resolveCellBackground(args: {
  weekday: number;
  occurrences: Occurrence[];
  override: Color | null | undefined;
  saturday: { fill: Color | null; textColor: Color | null };
  sunday: { fill: Color | null; textColor: Color | null };
  weekdayFill: Color | null;
}): CellBackground {
  const { occurrences } = args;
  if (args.override) return { tier: 'override', fill: args.override, textColor: null };
  const shaded = (kind: (k: EventKind) => boolean) => occurrences.find((o) => o.display.shade && kind(o.setKind));
  const pub = shaded((k) => k === 'public');
  if (pub) return { tier: 'public', fill: pub.display.background, textColor: pub.display.textColor };
  const school = shaded((k) => k === 'school');
  if (school) return { tier: 'school', fill: school.display.background, textColor: null };
  const other = shaded((k) => k !== 'public' && k !== 'school');
  if (other) return { tier: 'other', fill: other.display.background, textColor: null };
  if (args.weekday === 6 && (args.saturday.fill || args.saturday.textColor))
    return { tier: 'weekend', fill: args.saturday.fill, textColor: args.saturday.textColor };
  if (args.weekday === 0 && (args.sunday.fill || args.sunday.textColor))
    return { tier: 'weekend', fill: args.sunday.fill, textColor: args.sunday.textColor };
  return { tier: 'weekday', fill: args.weekdayFill, textColor: null };
}

/** Summarise differences between a project snapshot and the current library set. */
export interface EventSetDiff {
  added: EventItem[];
  removed: EventItem[];
  changed: { before: EventItem; after: EventItem }[];
  metadataChanged: string[];
}
const eventSig = (e: EventItem) => JSON.stringify([e.label, e.type, e.date, e.start, e.end, e.month, e.day, !!e.observed]);
export function diffEventSets(snapshot: EventSet, library: EventSet): EventSetDiff {
  const before = new Map(snapshot.events.map((e) => [e.id, e]));
  const after = new Map(library.events.map((e) => [e.id, e]));
  const added: EventItem[] = [];
  const removed: EventItem[] = [];
  const changed: { before: EventItem; after: EventItem }[] = [];
  for (const [id, e] of after) {
    const b = before.get(id);
    if (!b) added.push(e);
    else if (eventSig(b) !== eventSig(e)) changed.push({ before: b, after: e });
  }
  for (const [id, e] of before) if (!after.has(id)) removed.push(e);
  const metadataChanged: string[] = [];
  for (const k of ['name', 'source', 'verification', 'year', 'calendarType'] as const)
    if (JSON.stringify(snapshot[k]) !== JSON.stringify(library[k])) metadataChanged.push(k);
  return { added, removed, changed, metadataChanged };
}

/** Describe an event's date(s) for lists. */
export function describeEventDate(e: EventItem): string {
  if (e.type === 'date') return e.date ?? '';
  if (e.type === 'range') return `${e.start} → ${e.end}`;
  return `Every year on ${String(e.day).padStart(2, '0')}/${String(e.month).padStart(2, '0')} (dd/mm)`;
}

/** Validate one event item beyond its schema (ranges ordered, recurring day exists). */
export function validateEventItem(e: EventItem): string | null {
  if (!e.label.trim()) return 'Label is required';
  if (e.type === 'date' && !e.date) return 'Date is required';
  if (e.type === 'range') {
    if (!e.start || !e.end) return 'Start and end dates are required';
    if (keyToDayNumber(e.end) < keyToDayNumber(e.start)) return 'End date is before start date';
    if (keyToDayNumber(e.end) - keyToDayNumber(e.start) > 400) return 'Range is longer than 400 days';
  }
  if (e.type === 'recurring') {
    if (!e.month || !e.day) return 'Month and day are required';
    if (!parseDateKey(dateKey(2000, e.month, e.day))) return 'That month/day does not exist';
  }
  return null;
}

/**
 * Re-target a project's event sets at another year. Recurring events are kept;
 * year-specific events and dated sets (public/school) are flagged for review and
 * are never carried forward as verified.
 */
export function eventSetsForNewYear(sets: EventSet[], fromYear: number, toYear: number): {
  sets: EventSet[];
  reviewNotes: string[];
} {
  const reviewNotes: string[] = [];
  const out: EventSet[] = [];
  for (const s of sets) {
    if (s.year !== null && s.year !== undefined) {
      // A year-specific dataset (e.g. 2027 public holidays) is not valid for another year.
      reviewNotes.push(`"${s.name}" (${s.year}) was removed — select the ${toYear} ${s.kind === 'school' ? 'school calendar' : 'dataset'} from the library.`);
      continue;
    }
    const recurring = s.events.filter((e) => e.type === 'recurring');
    const dated = s.events.filter((e) => e.type !== 'recurring');
    const keptDated = dated.filter((e) => {
      const ys = [e.date, e.start, e.end].filter(Boolean).map((d) => +d!.slice(0, 4));
      return ys.some((y) => y === toYear);
    });
    const dropped = dated.length - keptDated.length;
    if (dropped > 0)
      reviewNotes.push(`"${s.name}": ${dropped} year-specific event(s) dated in ${fromYear} were not carried forward — re-enter them for ${toYear} if they still apply.`);
    out.push({
      ...s,
      events: [...recurring, ...keptDated],
      verification: s.verification === 'verified' ? 'unverified' : s.verification,
      verificationNote: s.verification === 'verified' ? `Carried forward from ${fromYear}; re-verify for ${toYear}.` : s.verificationNote,
    });
  }
  return { sets: out, reviewNotes };
}
